import React, { useState, useCallback, useRef } from "react";
import FileBrowser from "../../../shared/fileBrowser/FileBrowser";
import Button from "@mui/material/Button";
import styles from "./UploadNewTermsAndConditions.module.scss";
import Grid from "@mui/material/Grid";
import pdfIcon from "../../../imgs/pdf-acrobat-icon.svg";
import ClearIcon from "@mui/icons-material/Clear";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FileEncoder from "../../../utils/FileEncoder";
import { withTranslation } from "react-i18next";
import { InfoIconWithMessage } from "../../../shared/infoIcon/InfoIcon";
import TermsAndConditionsClient from "../../../shared/clients/TermsAndConditionsClient";
import { IS_CANADA_MARKET } from "../../../utils/EmpUtil";
import PendingActionClient from "../../../shared/clients/PendingActionClient";
import { TERMS_AND_CONDITIONS } from "../../../common/Constants";

function UploadNewTermsAndConditions({
                                       enableUpload,
                                       hideOrShow,
                                       user,
                                       loadTermsAndConditionsHistory,
                                       t
                                     }) {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [displayDetails, setDisplayDetails] = useState(false);
  const [enablePublish, setEnablePublish] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [displayError, setDisplayError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelected = useCallback((selectedFile) => {
    const MAX_FILE_SIZE = 1000000;
    const ALLOWED_TYPE = "application/pdf";

    if (selectedFile.size > MAX_FILE_SIZE || selectedFile.type !== ALLOWED_TYPE) {
      setDisplayError(true);
      setFile(null);
      setDisplayDetails(false);
      setEnablePublish(false);
    } else {
      setFile(selectedFile);
      setDisplayDetails(true);
      setDisplayError(false);
      setEnablePublish(false);
      setEffectiveDate("");
    }
  }, []);

  const handleUploadTermsAndConditions = useCallback(async () => {
    if (!enablePublish || !file || !effectiveDate || isUploading) {
      return;
    }

    setIsUploading(true);

    try {
      hideOrShow?.(true);

      const encodedFile = await new FileEncoder().encode(file);

      const formattedDate = `${effectiveDate.getDate()}/${effectiveDate.getMonth() + 1}/${effectiveDate.getFullYear()}`;

      const termsAndConditions = {
        file: encodedFile,
        nscId: user?.userId,
        effectiveDate: formattedDate,
      };

      await TermsAndConditionsClient.uploadTermsAndConditions(
        user,
        termsAndConditions
      );

      if (!IS_CANADA_MARKET(user?.market)) {
        await PendingActionClient.removeNscPendingAction(
          user,
          TERMS_AND_CONDITIONS,
          1
        );
      }

      loadTermsAndConditionsHistory?.();
      handleRemoveFile();

    } catch (error) {
      console.error('Error uploading terms and conditions:', error);
    } finally {
      setIsUploading(false);
      hideOrShow?.(false);
    }
  }, [
    enablePublish,
    file,
    effectiveDate,
    isUploading,
    hideOrShow,
    user,
    loadTermsAndConditionsHistory
  ]);

  const handleEffectiveDateChange = useCallback((date) => {
    if (date) {
      setEffectiveDate(date);
      setEnablePublish(true);
    } else {
      setEffectiveDate("");
      setEnablePublish(false);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setFile(null);
    setDisplayDetails(false);
    setEnablePublish(false);
    setEffectiveDate("");
    setDisplayError(false);
  }, []);

  const handleKeyDown = useCallback((event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  }, []);

  if (enableUpload === undefined) {
    return null;
  }

  return (
    <div className={styles.uploadTermsAndConditions}>
      {enableUpload ? (
        <>
          <div className="pagePadding">
            <InfoIconWithMessage
              message={t("BusinessSettings.termsandConditionsHeader")}
            />
          </div>

          <div className="pagePaddingLeft">
            <FileBrowser
              fileType=".pdf"
              onFileSelected={handleFileSelected}
              id="pdf-file"
              ref={fileInputRef}
            >
              <Button
                className={styles.uploadBtn}
                component="span"
                id="upload-file-btn"
                disabled={isUploading}
              >
                {t("BusinessSettings.uploadNewTermsAndConditions")}
              </Button>
            </FileBrowser>

            <p className={styles.fileTypeText}>
              {t("UploadTermsConditions.tipText")}
            </p>

            {displayError && (
              <div className={styles.warningDiv} role="alert">
                <div className={styles.warning} aria-hidden="true"></div>
                <div className={styles.errorText}>
                  {t("Error.fileError")}
                </div>
              </div>
            )}
          </div>

          {displayDetails && (
            <>
              <Grid
                container
                className={`${styles.effectiveBox} pagePaddingLeft`}
                id="effectiveRow"
              >
                <Grid item xs={4} className={styles.effectiveDateRow}>
                  <div className={styles.fileNameTextBox}>
                    <div>
                      <img
                        src={pdfIcon}
                        alt="PDF file icon"
                        aria-hidden="true"
                      />
                      <p title={file?.name}>{file?.name}</p>
                    </div>
                    <div
                      className={styles.clearImage}
                      id="removeFile"
                      onClick={handleRemoveFile}
                      onKeyDown={(e) => handleKeyDown(e, handleRemoveFile)}
                      role="button"
                      tabIndex={0}
                      aria-label={t("Common.removeFile")}
                    >
                      <ClearIcon />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={8} className={styles.effectiveDateRow}>
                  <span className={styles.effectiveText}>
                    {IS_CANADA_MARKET(user?.market)
                      ? t("BusinessSettings.dueDate")
                      : t("BusinessSettings.effectiveFrom")}
                  </span>
                  <DatePicker
                    selected={effectiveDate}
                    onChange={handleEffectiveDateChange}
                    id="effectiveDate"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yy"
                    minDate={new Date()}
                    disabled={isUploading}
                  />
                </Grid>
              </Grid>

              <div className={styles.publishButton}>
                <Button
                  type="button"
                  onClick={handleUploadTermsAndConditions}
                  className="greenBtn"
                  id="publish-action-btn"
                  disabled={!enablePublish || isUploading}
                >
                  {isUploading
                    ? t("Common.uploading")
                    : t("BusinessSettings.publish")
                  }
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <div id="termsHeaderWithWarning" className="pagePadding">
          <InfoIconWithMessage
            message={[
              t("BusinessSettings.termsandConditionsHeader"),
              " ",
              <span
                key="warning"
                className={styles.previewTitleWarning}
              >
                {t("BusinessSettings.maximumUploadWarning")}
              </span>,
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default withTranslation("emp")(UploadNewTermsAndConditions);
